import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BROWSABLE_CATEGORIES, ROUTES } from "@oshimap/config";
import type { MarkerViewModel } from "@oshimap/map-core";
import type { Bounds, LatLng } from "@oshimap/domain";
import { createBasemapAdapter } from "../../map/select-basemap";
import type { WebBasemapAdapter } from "../../map/web-basemap";
import { zoomForWidthKm, viewportBounds, TILE_SIZE } from "@oshimap/map-core";
import { clampZoom, type Camera } from "./use-map-gestures";
import { Button, Chip, EmptyState, ErrorState, Skeleton, CategoryIcon } from "../../components/ui";
import { useFavorites } from "../favorite/use-favorites";
import { session } from "../../lib/api";
import { AuthSheet } from "../auth/AuthSheet";
import { useAuthPrompt } from "../auth/useAuthPrompt";
import { PlaceCard } from "./PlaceCard";
import { useMapData, useMapLocations, type MapQuery } from "./use-map-data";
import { navigate } from "../../app/router";
import { play } from "../../lib/motion";
import "./map.css";

const SEOUL: LatLng = { latitude: 37.5445, longitude: 127.0557 };
type Snap = "peek" | "half" | "full";

export function MapScreen() {
  /*
   * 어댑터는 빌드가 정한다 (select-basemap.ts). 운영 빌드에 키가 없으면 여기서 던지고 — Mock 으로
   * 조용히 가지 않고 — 아래 providerError 화면이 이유를 말한다. 목록은 지도 없이도 열린다.
   */
  const [adapter, setAdapter] = useState<WebBasemapAdapter | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const basemapRef = useRef<HTMLDivElement>(null);
  const [ready, setReady] = useState(false);
  const [providerError, setProviderError] = useState<string | null>(null);
  const [snap, setSnap] = useState<Snap>("peek");
  const [selected, setSelected] = useState<MarkerViewModel | null>(null);
  const [category, setCategory] = useState<string | null>(null);
  const [showSearchHere, setShowSearchHere] = useState(false);

  const loc = useMapLocations(SEOUL);
  const fav = useFavorites();
  const auth = useAuthPrompt();

  useEffect(() => {
    if (providerError !== null) return;   /* 다시 시도는 providerError 를 지우면서 시작한다 */
    let cancelled = false;
    let made: WebBasemapAdapter | null = null;
    let off = () => {};
    const el = basemapRef.current;
    if (!el) return;
    /*
     * 고정 줌을 쓰면 폰과 데스크톱이 전혀 다른 넓이를 본다. "내 주변" 이
     * 기기마다 다른 뜻이 되지 않게, 보이는 **거리**(약 8km)를 맞추고 줌을 역산한다.
     */
    const zoom = zoomForWidthKm(8, el.clientWidth || 390, SEOUL.latitude);
    setCamera({ center: SEOUL, zoom });
    createBasemapAdapter()
      .then((a) => {
        if (cancelled) return;
        made = a;
        off = a.onProviderError((m) => { if (!cancelled) setProviderError(m); });
        return a.initialize(el, SEOUL, zoom).then(() => { if (!cancelled) { setAdapter(a); setReady(true); } });
      })
      .catch((e: unknown) => { if (!cancelled) setProviderError(e instanceof Error ? e.message : "지도를 불러오지 못했어요"); });
    return () => { cancelled = true; off(); made?.destroy(); setAdapter(null); setReady(false); };
  }, [providerError]);

  /*
   * 조회 시점은 사용자가 정한다.
   *
   * 지도를 움직일 때마다 자동으로 조회하면 "이 지역 검색" 버튼이 있을 이유가 없고,
   * 손가락을 떼기도 전에 목록이 계속 갈아엎힌다. 움직이면 버튼만 띄우고,
   * 실제 조회는 누를 때 한다.
   *
   * 다만 **컨테이너 크기가 처음 정해질 때**는 사용자가 움직인 게 아니라
   * 화면이 아직 안 그려졌던 것뿐이라, 그때는 바로 다시 읽는다.
   */
  /*
   * 카메라와 화면 크기를 **상태로 들고 있는다.**
   * 어댑터 안에 숨겨 두고 memo 안에서 읽으면 React 가 추적할 수 없는 부수효과가 되고,
   * 실제로 마커는 따라 움직이는데 조회 범위는 그대로인 상태가 나왔다.
   *
   * 조회 범위는 카메라와 크기로 계산한다 — 화면에 보이는 것과 조회하는 것이
   * 같은 값에서 나오므로 어긋날 수가 없다.
   */
  const [camera, setCamera] = useState<Camera>(() => ({ center: SEOUL, zoom: 14 }));
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [committed, setCommitted] = useState<Bounds | null>(null);

  /*
   * 조회 범위는 **지도가 실제로 보여 주는 영역**이다. 실제 SDK 가 있으면 그 bounds 를, Mock 이면
   * 카메라·크기에서 Mercator 로 계산한 것을 쓴다 — 화면에 보이는 것과 조회하는 것이 같은 값에서 나온다.
   */
  const visibleBounds = useCallback((cam: Camera, sz: { width: number; height: number }): Bounds | null => {
    const real = adapter?.ownsGestures ? adapter.getBounds() : null;
    if (real) return real;
    return sz.width > 0 ? viewportBounds(cam.center, cam.zoom, sz) : null;
  }, [adapter]);
  const commitViewport = useCallback(() => {
    setCommitted(visibleBounds(camera, size));
  }, [visibleBounds, camera, size]);

  /* 어댑터는 카메라를 따라온다 (Mock: 투영용 · SDK: 버튼 줌·클러스터 확대). SDK 가 스스로 움직인 뒤에는 아래 idle 이 상태를 맞춘다. */
  useEffect(() => { adapter?.setCenter(camera.center, camera.zoom); }, [adapter, camera]);
  useEffect(() => { adapter?.setViewportSize(size); }, [adapter, size]);
  useEffect(() => {
    if (!adapter?.ownsGestures) return;
    return adapter.onIdle(() => {
      const c = adapter.getCamera();
      if (!c) return;
      setCamera((prev) => {
        const moved = Math.abs(prev.center.latitude - c.center.latitude) > 1e-7
          || Math.abs(prev.center.longitude - c.center.longitude) > 1e-7 || Math.abs(prev.zoom - c.zoom) > 1e-6;
        if (!moved) return prev;
        setShowSearchHere(true);
        return { center: c.center, zoom: c.zoom };
      });
    });
  }, [adapter]);

  const query: MapQuery = useMemo(() => {
    const base: Bounds = committed
      ?? { north: SEOUL.latitude + 0.02, south: SEOUL.latitude - 0.02, east: SEOUL.longitude + 0.03, west: SEOUL.longitude - 0.03 };
    return category ? { mode: "bounds", bounds: base, category } : { mode: "bounds", bounds: base };
  }, [committed, category]);

  const visitedKeys = useMemo(() => new Set<string>(), []);
  const { state, markers, reload } = useMapData(query, visitedKeys);

  const withFav = useMemo(
    () => markers.map((m) => ({ ...m, isFavorited: fav.isFavorited(m.isEvent ? "EVENT" : "PLACE", m.id) })),
    [markers, fav],
  );

  /* 상세는 라우터가 연다 — 지도 안에서만 열면 URL 이 생기지 않는다 (IA-4). */
  const openPlace = useCallback((m: MarkerViewModel) => {
    navigate(m.isEvent ? ROUTES.event(m.id) : ROUTES.place(m.id));
  }, []);

  /*
   * 클러스터를 누르면 묶인 것을 푼다. 첫 멤버를 골라 버리면 나머지는 없는 셈이 된다.
   * 멤버들이 서로 떨어져 있으면 그 범위에 맞추고, 거의 같은 자리면 한 단계만 더 들어간다
   * (최대 줌까지 밀어 넣어도 30m 떨어진 두 곳은 안 갈라진다).
   */

  const onSelect = useCallback((m: MarkerViewModel) => {
    setSelected(m);
    setSnap((s) => (s === "peek" ? "half" : s));
  }, []);

  const onClusterOpen = useCallback((node: {
    latitude: number; longitude: number;
    members: readonly { latitude: number; longitude: number }[];
    firstKey: string | null;
  }) => {
    const lats = node.members.map((m) => m.latitude);
    const lngs = node.members.map((m) => m.longitude);
    const span = Math.max(Math.max(...lats) - Math.min(...lats), Math.max(...lngs) - Math.min(...lngs));

    /*
     * 서로 30m 쯤 떨어진 두 곳은 최대 줌까지 밀어 넣어도 갈라지지 않는다.
     * 그때까지 계속 확대만 하면 눌러도 아무 일이 없는 것처럼 보인다 —
     * 갈라질 수 없으면 확대 대신 **첫 곳을 골라서** 카드를 띄운다.
     * 나머지는 어차피 아래 목록에 다 있다.
     */
    const degPerPx = 360 / (TILE_SIZE * Math.pow(2, camera.zoom));
    const separable = span / degPerPx > 52;      /* 클러스터 반경 */

    if (!separable && node.firstKey) {
      const m = markers.find((x) => x.key === node.firstKey);
      if (m) { onSelect(m); return; }
    }
    setCamera((c) => ({
      center: { latitude: node.latitude, longitude: node.longitude },
      zoom: clampZoom(c.zoom + 2),
    }));
    setShowSearchHere(true);
  }, [camera.zoom, markers, onSelect]);

  const toggleFav = useCallback(
    async (m: MarkerViewModel): Promise<boolean> => {
      const target = m.isEvent ? ("EVENT" as const) : ("PLACE" as const);
      if (session.state.status !== "authenticated") {
        /* requireAuth 가 성공 시 action 을 즉시 실행하므로, 게스트일 때만 통과시킨다. */
        auth.require("저장하려면 로그인이 필요해요", () => { void fav.toggle(target, m.id); });
        return false;
      }
      return fav.toggle(target, m.id);
    },
    [fav, auth],
  );

  /* NFR-A-08 / NFR-R-01: 지도가 죽어도 목록으로 같은 곳을 찾을 수 있어야 한다. */
  if (providerError) {
    return (
      <div className="om-screen om-screen--pad">
        <h1 className="sr-only">지도</h1>
        <ErrorState title="지도를 불러오지 못했어요" body={providerError} onRetry={() => { setProviderError(null); setReady(false); }} />
        <h2 className="om-h2">지도 없이 둘러보기</h2>
        <ResultList state={state} markers={withFav} onOpen={openPlace} onToggleFavorite={toggleFav} onRetry={reload} />
      </div>
    );
  }

  return (
    <div className="om-screen">
      <h1 className="sr-only">지도</h1>
      <div ref={containerRef} style={{ position: "absolute", inset: 0 }}>
        {/* 실제 SDK 는 자기 DOM 을 여기에 그린다. Mock 은 비워 둔다. 핀 층(MapCanvas)은 그 위에 겹친다. */}
        <div ref={basemapRef} className="om-basemap" aria-hidden={!ready} />
        {ready && adapter ? (
          <MapCanvasLazy adapter={adapter} markers={withFav} selectedKey={selected?.key ?? null}
            userLocation={loc.userLocation} onSelect={onSelect} onClusterOpen={onClusterOpen}
            camera={camera}
            onCamera={(next) => { setCamera(next); setShowSearchHere(true); }}
            onResized={(s) => { setSize(s); setCommitted(visibleBounds(camera, s)); }} />
        ) : null}
      </div>

      <div className="om-map-top">
        <div className="om-searchbar">
          <span aria-hidden="true">🔍</span>
          <span>장소 · 이벤트 · 작품 검색</span>
        </div>
        <div className="om-filters" role="group" aria-label="카테고리 필터">
          <Chip pressed={category === null} onClick={(e) => { setCategory(null); play(e.currentTarget, "mo-pop"); setShowSearchHere(true); }}>전체</Chip>
          {/*
            * 8개 전부 보여준다. 5개만 두면 전시·공연·성지순례는 지도에서
            * **아예 거를 수 없다** — 줄이 길어지는 건 가로 스크롤이 해결하고,
            * 카테고리 목록 자체가 제품 결정(OD-018)이라 임의로 자르면 안 된다.
            */}
          {BROWSABLE_CATEGORIES.map((c) => (
            <Chip key={c.code} pressed={category === c.code}
              onClick={(e) => { setCategory(c.code); play(e.currentTarget, "mo-pop"); setShowSearchHere(true); }}>
              <CategoryIcon code={c.code} size={17} />{c.label}
            </Chip>
          ))}
        </div>
      </div>

      {showSearchHere ? (
        <button className="om-searchhere mo-fadeup"
          onClick={() => { setShowSearchHere(false); commitViewport(); }}>
          이 지역 검색
        </button>
      ) : null}

      {/* 휠도 핀치도 못 쓰는 사람이 있다. 버튼으로도 줌할 수 있어야 한다. */}
      <div className="om-zoom">
        <button type="button" aria-label="확대"
          onClick={() => { setCamera((c) => ({ ...c, zoom: clampZoom(c.zoom + 1) })); setShowSearchHere(true); }}>＋</button>
        <button type="button" aria-label="축소"
          onClick={() => { setCamera((c) => ({ ...c, zoom: clampZoom(c.zoom - 1) })); setShowSearchHere(true); }}>−</button>
      </div>

      <div className="om-fabs">
        <button className="om-fab" aria-label="현재 위치" onClick={() => { void loc.locate(); }}>◎</button>
        <button className="om-fab om-fab--accent" aria-label="장소 제보"
          onClick={() => navigate(ROUTES.submit)}>＋</button>
      </div>

      {auth.reason ? <AuthSheet reason={auth.reason} onClose={auth.close} /> : null}

      <div className="om-sheet" data-snap={snap}>
        <button className="om-sheet__grip" aria-label={`시트 ${snap === "full" ? "접기" : "펼치기"}`}
          onClick={() => setSnap((s) => (s === "peek" ? "half" : s === "half" ? "full" : "peek"))}>
          <i />
        </button>
        <div className="om-sheet__body">
          {loc.permission === "denied" ? (
            <p className="om-note">위치 권한이 꺼져 있어 지역 기준으로 보여드려요. 설정에서 언제든 켤 수 있어요.</p>
          ) : null}
          {fav.error ? <p className="om-note om-note--err" role="status">{fav.error}</p> : null}
          {selected ? (
            <>
              <PlaceCard m={selected} selected onOpen={openPlace} onToggleFavorite={toggleFav} />
              <div className="om-sheet__cta">
                <Button variant="primary" block onClick={() => openPlace(selected)}>자세히 보기</Button>
              </div>
              <SheetHeading state={state} count={withFav.length} />
            </>
          ) : (
            <SheetHeading state={state} count={withFav.length} />
          )}
          <ResultList state={state} markers={withFav.filter((m) => m.key !== selected?.key)}
            onOpen={openPlace} onToggleFavorite={toggleFav} onRetry={reload} />
        </div>
      </div>
    </div>
  );
}

/**
 * 못 불러온 것을 "0곳" 이라고 말하지 않는다.
 * 없다고 단언하면 사람이 발길을 돌린다 — 헛걸음은 이 제품이 없애려는 바로 그 경험이다.
 */
function SheetHeading({ state, count }: { state: { status: string }; count: number }) {
  if (state.status === "loading" || state.status === "idle") {
    return <h2 className="om-h2">이 지역 찾는 중…</h2>;
  }
  if (state.status === "error") {
    return <h2 className="om-h2">이 지역</h2>;
  }
  return <h2 className="om-h2">이 지역 <span className="num">{count}</span>곳</h2>;
}

function ResultList({ state, markers, onOpen, onToggleFavorite, onRetry }: {
  state: ReturnType<typeof useMapData>["state"];
  markers: MarkerViewModel[];
  onOpen: (m: MarkerViewModel) => void;
  onToggleFavorite: (m: MarkerViewModel) => Promise<boolean>;
  onRetry: () => void;
}) {
  if (state.status === "loading" || state.status === "idle") return <Skeleton lines={3} />;
  if (state.status === "error") {
    return <ErrorState level={3} title="목록을 불러오지 못했어요" body={state.error.message} onRetry={onRetry} />;
  }
  if (markers.length === 0) {
    return (
      <EmptyState
        level={3}
        title="이 지역에는 아직 등록된 곳이 없어요"
        body="범위를 넓히거나, 알고 계신 곳을 제보해 주세요."
        action={<Button variant="secondary" size="sm" onClick={() => navigate(ROUTES.submit)}>제보하기</Button>}
      />
    );
  }
  return (
    <div className="om-list">
      {markers.map((m) => (
        <PlaceCard key={m.key} m={m} onOpen={onOpen} onToggleFavorite={onToggleFavorite} />
      ))}
    </div>
  );
}

/* MapCanvas 는 DOM 크기에 의존하므로 컨테이너 마운트 뒤에만 그린다. */
import { MapCanvas } from "./MapCanvas";
function MapCanvasLazy(props: Parameters<typeof MapCanvas>[0]) { return <MapCanvas {...props} />; }
